import {
  Lightning,
  TrendUp,
  Globe,
  Target,
} from "@phosphor-icons/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { StatCard } from "./StatCard";

const CHART_COLORS = ["#a855f7", "#10b981", "#ef4444", "#f59e0b", "#8b5cf6", "#06b6d4", "#84cc16", "#f43f5e"];

const TOOLTIP_STYLE_DARK = {
  background: "#12121a",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "8px",
  fontSize: "12px",
  color: "#fff"
};
const TOOLTIP_STYLE_LIGHT = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  fontSize: "12px"
};
const TICK_STYLE_DARK = { fontSize: 11, fill: '#6b7280' };
const TICK_STYLE_LIGHT = { fontSize: 11, fill: '#9ca3af' };
const DOT_STYLE = { fill: "#a855f7", strokeWidth: 2 };
const Y_AXIS_DOMAIN = [0, 100];

const LABEL_LINE_STYLE = { stroke: '#888', strokeWidth: 1 };

export function TrendsTab({ trends, stats, isDark }) {
  const probabilityData = trends.find(t => t.name === "Probability Trends")?.data || [];
  const categoryData = trends.find(t => t.name === "Category Distribution")?.data || [];
  const tooltipStyle = isDark ? TOOLTIP_STYLE_DARK : TOOLTIP_STYLE_LIGHT;
  const tickStyle = isDark ? TICK_STYLE_DARK : TICK_STYLE_LIGHT;
  const gridColor = isDark ? "#1f1f2e" : "#e2e8f0";
  const strokeColor = isDark ? "#374151" : "#94a3b8";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="trends-content">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Opportunities" value={stats.total_opportunities} icon={Lightning} isDark={isDark} />
          <StatCard label="High Probability (75%+)" value={stats.high_probability_count} icon={Target} color="text-emerald-500" isDark={isDark} />
          <StatCard label="Average Probability" value={`${stats.average_probability}%`} icon={TrendUp} color="text-purple-500" isDark={isDark} />
          <StatCard label="Regions Covered" value={stats.regions_covered?.length || 4} icon={Globe} color="text-purple-600" isDark={isDark} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`rounded-xl p-6 border ${isDark ? 'bg-[#12121a] border-white/10' : 'bg-white border-gray-200'}`} data-testid="probability-chart">
          <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Probability Trends</h3>
          {probabilityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={probabilityData}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="date" tick={tickStyle} stroke={strokeColor} />
                <YAxis tick={tickStyle} stroke={strokeColor} domain={Y_AXIS_DOMAIN} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line 
                  type="monotone" 
                  dataKey="probability" 
                  stroke="#a855f7" 
                  strokeWidth={2}
                  dot={DOT_STYLE}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className={`h-[300px] flex items-center justify-center font-body ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Generate more opportunities to see trends
            </div>
          )}
        </div>

        <div className={`rounded-xl p-6 border ${isDark ? 'bg-[#12121a] border-white/10' : 'bg-white border-gray-200'}`} data-testid="category-chart">
          <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Category Distribution</h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={LABEL_LINE_STYLE}
                >
                  {categoryData.map((entry) => (
                    <Cell key={entry.name} fill={CHART_COLORS[categoryData.indexOf(entry) % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className={`h-[300px] flex items-center justify-center font-body ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Generate more opportunities to see distribution
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
